import { createStore } from "vuex";
import { getFcmToken } from "@/utils/MobileHybridApplication";
import {
  fetchBags,
  fetchCategoriesV1,
  fetchTopSearches,
  logIn,
  logOut,
} from "@/api";
import CartPlugin from "@/store/CartPlugin";
import { loadSearchHistory } from "@/store/SearchStorage";
import SearchPlugin from "@/store/SearchPlugin";
import RecentPlugin from "@/store/RecentPlugin";
import { loadRecentProductHistory } from "@/store/RecentStorage";
import {
  fetchPromotionHotTags,
  fetchSessionUser,
  fetchTotalFavorites,
} from "@/api/v4";

export default createStore({
  state() {
    return {
      loading: true,
      visibleBackgroundMask: false,
      visibleProductFilter: false,

      visibleAllCategories: false,
      visibleMobileSearchTab: false,

      userId: null,
      name: null,
      custNo: null,
      telephone: null,
      email: null,
      gender: null,
      grade: null,
      recommendUsername: null,
      chnlLargeCd: null,
      chnlMiddleCd: null,

      cartItems: [],
      favoriteProductPage: {
        content: [],
        numberOfElements: 0,
      },
      totalFavorites: 0,
      /**
       * @type GetPromotionHotTagsResponseDto[]
       */
      hotTags: [],
      categories: [],
      searchHistory: JSON.parse(loadSearchHistory() || "[]"),
      maxSearchHistorySize: 10,
      topSearchKeywords: [],
      recentProductHistory: JSON.parse(loadRecentProductHistory() || "[]"),
      maxRecentProductHistorySize: 5,

      bagId: null,
      categoryAll: false,
    };
  },
  getters: {
    visibleAllCategories: (state) => state.visibleAllCategories || false,
    visibleMobileSearchTab: (state) => state.visibleMobileSearchTab || false,
    userId: (state) => state.userId,
    custNo: (state) => state.custNo,
    name: (state) => state.name,
    userName: (state) => state.name,
    cartItems: (state) => state.cartItems,
    cartItemLength: (state) => state.cartItems.length,
    totalFavorites: (state) => state.totalFavorites || 0,
    hotTags: (state) => state.hotTags || [],
    golfCategories: (state) =>
      state.categories.filter((category) => category.groups === 1),
    golfCategoriesDepth2: (_, getters) =>
      getters.golfCategories.filter((golfCategory) => golfCategory.depth === 2),
    golfCategoriesDepth3: (_, getters) =>
      getters.golfCategories.filter((golfCategory) => golfCategory.depth === 3),
    livingCategories: (state) =>
      state.categories.filter((category) => category.groups === 2),
    livingCategoriesDepth2: (_, getters) =>
      getters.livingCategories.filter(
        (livingCategory) => livingCategory.depth === 2
      ),
    livingCategoriesDepth3: (_, getters) =>
      getters.livingCategories.filter(
        (livingCategory) => livingCategory.depth === 3
      ),
    searchHistory: (state) => state.searchHistory || [],
    maxSearchHistorySize: (state) => state.maxSearchHistorySize || 0,
    topSearchKeywords: (state) => state.topSearchKeywords || [],
    recentProductHistory: (state) => state.recentProductHistory || [],
    isMemberVIP: (state) => {
      return ["J1", "VIP"].includes(state.grade?.toUpperCase?.());
    },
  },
  actions: {
    async logIn(context, token) {
      const { data: member } = await logIn({ tokenNo: token });

      await context.commit("saveMember", member);

      getFcmToken();
    },
    async logOut(context) {
      await logOut();
      context.commit("removeMember");
    },
    async loadBags(context) {
      if (!context.getters.userId) {
        return;
      }

      const res = await fetchBags({
        chooseDelivery: "a",
      });

      const cartItems = res.data.data || [];
      context.commit("setCartItems", cartItems);
      context.commit("setCartItemLength", cartItems.length); // deprecated
    },
    async loadTotalFavorites(context) {
      if (!context.getters.userId) {
        return;
      }

      const res = await fetchTotalFavorites({
        memberId: context.getters.userId,
      });

      context.commit("setTotalFavorites", res.data.data || 0);
    },
    async loadSessionUser(context) {
      const res = await fetchSessionUser();

      const sessionUser = res.data.data || {};
      context.commit("saveMember", sessionUser);
    },
    async loadHotTags(context) {
      const res = await fetchPromotionHotTags();

      const hotTags = res.data.data || [];
      context.commit("setHotTags", hotTags);
    },
    async loadCategories(context) {
      const res = await fetchCategoriesV1();

      const categories = res.data.data || [];
      context.commit("setCategories", categories);
    },
    async loadTopSearchKeywords(context) {
      const res = await fetchTopSearches();

      const topSearchKeywords = res.data.data || [];
      context.commit("setTopSearchKeywords", topSearchKeywords);
    },
    async writeSearchHistoryLog(context, q) {
      const searchHistory = context.state.searchHistory;
      const maxSearchHistorySize = context.state.maxSearchHistorySize;
      const log = q.trim();
      if (!log) {
        return;
      }

      const newSearchHistory = searchHistory.filter(({ text }) => text !== log);
      newSearchHistory.unshift({ text: log });
      if (maxSearchHistorySize < newSearchHistory.length) {
        newSearchHistory.pop();
      }

      context.commit("setSearchHistory", newSearchHistory);
    },
    async writeRecentProductLog(context, product) {
      const recentProductHistory = context.state.recentProductHistory;
      const maxRecentProductHistorySize =
        context.state.maxRecentProductHistorySize;

      const newRecentProductHistory = recentProductHistory.filter(
        ({ id }) => id !== product.id
      );
      newRecentProductHistory.push({
        image: product.mainImageUrl,
        id: product.id,
      });
      if (maxRecentProductHistorySize < newRecentProductHistory.length) {
        newRecentProductHistory.shift();
      }

      context.commit("setRecentProductHistory", newRecentProductHistory);
    },
    async removeRecentProductLog(context, id) {
      const recentProductHistory = context.state.recentProductHistory;

      const newRecentProductHistory = recentProductHistory.filter(
        (log) => id !== log.id
      );
      context.commit("setRecentProductHistory", newRecentProductHistory);
    },
  },
  mutations: {
    setLoading(state, loading) {
      if (loading) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "auto";
      }

      state.loading = loading;
    },
    setVisibleBackgroundMask(state, visibleBackgroundMask) {
      state.visibleBackgroundMask = visibleBackgroundMask || false;
    },
    setVisibleProductFilter(state, visibleProductFilter) {
      state.visibleProductFilter = visibleProductFilter || false;
    },
    setVisibleAllCategories(state, visibleAllCategories) {
      state.visibleAllCategories = visibleAllCategories || false;
    },
    setVisibleMobileSearchTab(state, visibleMobileSearchTab) {
      state.visibleMobileSearchTab = visibleMobileSearchTab || false;
    },
    saveMember(state, member) {
      state.userId = member?.id || null;
      state.name = member?.name || null;
      state.custNo = member?.custNo || null;
      state.telephone = member?.telephone || null;
      state.email = member?.email || null;
      state.gender = member?.gender || null;
      state.grade = member?.grade || null;
      state.recommendUsername = member?.recommendUsername || null;
      state.chnlLargeCd = member?.chnlLargeCd || null;
      state.chnlMiddleCd = member?.chnlMiddleCd || null;
    },
    removeMember(state) {
      state.userId = null;
      state.name = null;
      state.custNo = null;
      state.telephone = null;
      state.email = null;
      state.gender = null;
      state.grade = null;
      state.recommendUsername = null;

      localStorage.removeItem("TTL");
      localStorage.removeItem("shAI");
      localStorage.removeItem("GI");
      localStorage.removeItem("buy");
      localStorage.removeItem("cartLength");
    },
    setCartItems(state, cartItems) {
      state.cartItems = cartItems || [];
    },
    setCartItemLength(state, cartItemLength) {
      state.cartItemLength = cartItemLength || 0;
    },
    setTotalFavorites(state, totalFavorites) {
      state.totalFavorites = totalFavorites || 0;
    },
    setHotTags(state, hotTags) {
      state.hotTags = hotTags || [];
    },
    setCategories(state, categories) {
      state.categories = categories || [];
    },
    setSearchHistory(state, searchHistory) {
      state.searchHistory = searchHistory || [];
    },
    setTopSearchKeywords(state, topSearchKeywords) {
      state.topSearchKeywords = topSearchKeywords || [];
    },
    setRecentProductHistory(state, recentProductHistory) {
      state.recentProductHistory = recentProductHistory || [];
    },
    saveBagIds(state, bagIds = []) {
      state.bagId = bagIds;
    },
  },
  plugins: [CartPlugin, SearchPlugin, RecentPlugin],
});
